import { useEffect, useRef, useState, useCallback } from 'react';
import { API_BASE_URL } from '../services/api';

// ──────────────────────────────────────────────────────────────────────────
// useLivePrices — Server-Sent Events stream of universe live prices.
//
// Opens a single persistent EventSource to /api/stream/prices and keeps a
// merged item map up to date. Emits a per-symbol "flash" signal (up/down)
// whenever a price changes, so tables can flash cells like an exchange
// terminal. EventSource auto-reconnects on drop.
//
// Zero extra backend/yfinance load — the stream only relays the existing
// in-memory UniversePriceCache (browser <-> our backend only).
// ──────────────────────────────────────────────────────────────────────────

export interface LivePriceItem {
  symbol: string;
  company_name?: string;
  price: number | null;
  chg_1d: number | null;
  [key: string]: any;
}

export type FlashDir = 'up' | 'down';

interface UseLivePricesResult {
  items: LivePriceItem[];
  lastUpdated: string | null;
  connected: boolean;
  /** symbol -> flash direction, auto-clears ~800ms after each change */
  flashes: Record<string, FlashDir>;
}

const FLASH_MS = 5500;  // keep the colour ~5s post-change (matches CSS animation)

export function useLivePrices(enabled: boolean = true): UseLivePricesResult {
  const [items, setItems] = useState<LivePriceItem[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [flashes, setFlashes] = useState<Record<string, FlashDir>>({});

  const itemMap = useRef<Map<string, LivePriceItem>>(new Map());
  const flashTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const clearFlash = useCallback((sym: string) => {
    setFlashes(f => {
      if (!(sym in f)) return f;
      const next = { ...f };
      delete next[sym];
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const _tok = localStorage.getItem('auth_token');
    const url = `${API_BASE_URL}/api/stream/prices${_tok ? `?token=${_tok}` : ''}`;
    const es = new EventSource(url);

    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false); // EventSource retries automatically

    const onSnapshot = (e: MessageEvent) => {
      try {
        const snap = JSON.parse(e.data);
        const map = new Map<string, LivePriceItem>();
        for (const it of snap.items || []) map.set(it.symbol, it);
        itemMap.current = map;
        setItems(snap.items || []);
        setLastUpdated(snap.last_updated ?? null);
      } catch { /* ignore malformed frame */ }
    };

    const onUpdate = (e: MessageEvent) => {
      try {
        const upd = JSON.parse(e.data);
        const newFlashes: Record<string, FlashDir> = {};
        for (const it of upd.items || []) {
          const prev = itemMap.current.get(it.symbol);
          if (prev && prev.price != null && it.price != null && it.price !== prev.price) {
            newFlashes[it.symbol] = it.price > prev.price ? 'up' : 'down';
          }
          itemMap.current.set(it.symbol, it);
        }
        setItems(Array.from(itemMap.current.values()));
        setLastUpdated(upd.last_updated ?? null);

        const syms = Object.keys(newFlashes);
        if (syms.length) {
          setFlashes(f => ({ ...f, ...newFlashes }));
          for (const sym of syms) {
            if (flashTimers.current[sym]) clearTimeout(flashTimers.current[sym]);
            flashTimers.current[sym] = setTimeout(() => clearFlash(sym), FLASH_MS);
          }
        }
      } catch { /* ignore malformed frame */ }
    };

    es.addEventListener('snapshot', onSnapshot as EventListener);
    es.addEventListener('update', onUpdate as EventListener);

    return () => {
      es.removeEventListener('snapshot', onSnapshot as EventListener);
      es.removeEventListener('update', onUpdate as EventListener);
      es.close();
      Object.values(flashTimers.current).forEach(clearTimeout);
      flashTimers.current = {};
    };
  }, [enabled, clearFlash]);

  return { items, lastUpdated, connected, flashes };
}
